import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";

import About from "@/pages/About";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import PendingApproval from "@/pages/PendingApproval";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import StudentDashboard from "@/pages/StudentDashboard";
import Practice from "@/pages/Practice";
import Result from "@/pages/Result";
import Leaderboard from "@/pages/Leaderboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminStudents from "@/pages/AdminStudents";
import AdminTeachers from "@/pages/AdminTeachers";
import AdminDemos from "@/pages/AdminDemos";
import AdminExercises from "@/pages/AdminExercises";
import AdminExerciseEditor from "@/pages/AdminExerciseEditor";
import AdminExerciseFromPhoto from "@/pages/AdminExerciseFromPhoto";
import AdminResults from "@/pages/AdminResults";
import AdminAttemptDetail from "@/pages/AdminAttemptDetail";
import AdminVideos from "@/pages/AdminVideos";
import AdminPayments from "@/pages/AdminPayments";
import AdminCoupons from "@/pages/AdminCoupons";
import StudentVideos from "@/pages/StudentVideos";
import Community from "@/pages/Community";
import CommunityThread from "@/pages/CommunityThread";
import AdminCommunityModeration from "@/pages/AdminCommunityModeration";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="text-stone font-heading">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/about" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/student"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/pending-approval" element={<ProtectedRoute><PendingApproval /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Student */}
          <Route path="/student" element={<ProtectedRoute role="student"><StudentDashboard /></ProtectedRoute>} />
          <Route path="/practice/:attemptId" element={<ProtectedRoute role="student"><Practice /></ProtectedRoute>} />
          <Route path="/result/:attemptId" element={<ProtectedRoute role="student"><Result /></ProtectedRoute>} />
          <Route path="/student/leaderboard" element={<ProtectedRoute role="student"><Leaderboard /></ProtectedRoute>} />
          <Route path="/student/videos" element={<ProtectedRoute role="student"><StudentVideos /></ProtectedRoute>} />
          <Route path="/student/community" element={<ProtectedRoute role="student"><Community /></ProtectedRoute>} />
          <Route path="/student/community/:threadId" element={<ProtectedRoute role="student"><CommunityThread /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/students" element={<ProtectedRoute role="admin"><AdminStudents /></ProtectedRoute>} />
          <Route path="/admin/teachers" element={<ProtectedRoute role="admin"><AdminTeachers /></ProtectedRoute>} />
          <Route path="/admin/demos" element={<ProtectedRoute role="admin"><AdminDemos /></ProtectedRoute>} />
          <Route path="/admin/exercises" element={<ProtectedRoute role="admin"><AdminExercises /></ProtectedRoute>} />
          <Route path="/admin/exercises/:exerciseId" element={<ProtectedRoute role="admin"><AdminExerciseEditor /></ProtectedRoute>} />
          <Route path="/admin/exercises/new" element={<ProtectedRoute role="admin"><AdminExerciseEditor /></ProtectedRoute>} />
          <Route path="/admin/exercises/new-from-photo" element={<ProtectedRoute role="admin"><AdminExerciseFromPhoto /></ProtectedRoute>} />
          <Route path="/admin/results" element={<ProtectedRoute role="admin"><AdminResults /></ProtectedRoute>} />
          <Route path="/admin/videos" element={<ProtectedRoute role="admin"><AdminVideos /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute role="admin"><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/coupons" element={<ProtectedRoute role="admin"><AdminCoupons /></ProtectedRoute>} />
          <Route path="/admin/community" element={<ProtectedRoute role="admin"><Community /></ProtectedRoute>} />
          <Route path="/admin/community/moderation" element={<ProtectedRoute role="admin"><AdminCommunityModeration /></ProtectedRoute>} />
          <Route path="/admin/community/:threadId" element={<ProtectedRoute role="admin"><CommunityThread /></ProtectedRoute>} />
          <Route path="/admin/attempts/:attemptId" element={<ProtectedRoute role="admin"><AdminAttemptDetail /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
